import { useCallback, useEffect, useRef, useState } from "react";
import { projectApi } from "../services/api";
import { fetchAllPages } from "./utils";

/**
 * Self-contained fetch hook for the Projects resource.
 *
 * Returns { projects, loading, error, refresh, createProject, updateProject,
 * deleteProject }. The mutation helpers throw on failure so callers can
 * surface field-level errors from the modal; the list-refresh side effect
 * is triggered automatically on success.
 */
const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // See useTasks: multi-page fetches can overlap, and the loser must not
  // win. The board's project filter is built from this list, so a stale
  // empty result here empties the filter too.
  const requestRef = useRef(0);

  const fetchProjects = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      // Follow `next`: page 1 alone silently caps the filter at 12 projects.
      const { items } = await fetchAllPages((page) =>
        projectApi.getAll({ page }),
      );
      if (request !== requestRef.current) return;
      setProjects(items);
    } catch (err) {
      if (request !== requestRef.current) return;
      setError(err);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = useCallback(
    async (data) => {
      const response = await projectApi.create(data);
      await fetchProjects();
      return response.data;
    },
    [fetchProjects],
  );

  const updateProject = useCallback(
    async (id, data) => {
      const response = await projectApi.update(id, data);
      await fetchProjects();
      return response.data;
    },
    [fetchProjects],
  );

  const deleteProject = useCallback(
    async (id) => {
      await projectApi.delete(id);
      await fetchProjects();
    },
    [fetchProjects],
  );

  return {
    projects,
    loading,
    error,
    refresh: fetchProjects,
    createProject,
    updateProject,
    deleteProject,
  };
};

export default useProjects;
